import { useEffect, useState } from "react";

import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  TablePagination,
  Paper,
  TextField,
  InputAdornment,
  Switch,
  Tooltip,
  TableSortLabel,
  Typography,
} from "@mui/material";
import PageContainer from "../../../components/container/PageContainer";

import FeatherIcon from "feather-icons-react";

import SearchIcon from "@mui/icons-material/Search";

import CloseIcon from "@mui/icons-material/Close";

import EditIcon from "@mui/icons-material/Edit";

import FilterAltIcon from "@mui/icons-material/FilterAlt";

import { toast } from "react-toastify";

import { useFormik } from "formik";

import * as Yup from "yup";

import {
  GetRequestTypeList,
  AddRequestType,
  UpdateRequestType,
} from "../../../services/admin";

import CustomFormLabel from "../../../components/forms/custom-elements/CustomFormLabel";

import CustomTextField from "../../../components/forms/custom-elements/CustomTextField";

const RequestTypeMaster = () => {
  const [list, setList] = useState([]);

  const [editId, setEditId] = useState(null);

  const [page, setPage] = useState(0);

  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [globalSearch, setGlobalSearch] = useState("");

  const [showAllFilters, setShowAllFilters] = useState(false);

  const [searchText, setSearchText] = useState({});

  const [order, setOrder] = useState("asc");

  const [orderBy, setOrderBy] = useState("");

  useEffect(() => {
    fetchRequestTypes();
  }, []);

  // =========================================
  // FETCH REQUEST TYPES
  // =========================================

  const fetchRequestTypes = async () => {
    try {
      const response = await GetRequestTypeList();

      const formattedData = (response?.data || []).map((item) => ({
        id: item.Id || item.id,

        name: item.Name || item.name,

        IsActive: Boolean(item.IsActive ?? item.isActive),
      }));

      setList(formattedData);
    } catch (error) {
      console.log(error);

      toast.error("Failed to load request types");
    }
  };

  // =========================================
  // VALIDATION
  // =========================================

  const validationSchema = Yup.object({
    name: Yup.string()
      .trim()
      .required("Request type is required")
      .matches(/^[A-Za-z0-9\s&_()-]+$/, "Invalid request type")
      .test(
        "duplicate-request-type",
        "Request type already exists",
        function (value) {
          if (!value) return true;

          const isDuplicate = list.some(
            (item) =>
              item.name?.toLowerCase().trim() ===
                value.toLowerCase().trim() &&
              item.id !== editId
          );

          return !isDuplicate;
        }
      ),
  });

  // =========================================
  // FORMIK
  // =========================================

  const formik = useFormik({
    initialValues: {
      name: "",

      IsActive: true,
    },

    validationSchema,

    onSubmit: async (values, { resetForm }) => {
      try {
        const payload = {
          Id: editId || 0,

          Name: values.name.trim(),

          IsActive: values.IsActive ? 1 : 0,
        };

        if (editId) {
          await UpdateRequestType(payload);

          toast.success("Request type updated successfully");
        } else {
          await AddRequestType(payload);

          toast.success("Request type added successfully");
        }

        fetchRequestTypes();

        resetForm();

        setEditId(null);
      } catch {
        toast.error("Save failed");
      }
    },
  });

  // =========================================
  // EDIT
  // =========================================

  const handleEdit = (item) => {
    setEditId(item.id);

    formik.setValues({
      name: item.name,

      IsActive: item.IsActive,
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // =========================================
  // RESET
  // =========================================

  const resetForm = () => {
    setEditId(null);

    formik.resetForm();
  };

  // =========================================
  // TOGGLE STATUS
  // =========================================

  const handleToggleStatus = async (item, checked) => {
    setList((prev) =>
      prev.map((x) =>
        x.id === item.id
          ? {
              ...x,
              IsActive: checked,
            }
          : x
      )
    );

    try {
      const payload = {
        Id: item.id,

        Name: item.name,

        IsActive: checked ? 1 : 0,
      };

      await UpdateRequestType(payload);

      toast.success(
        `Request Type ${checked ? "Activated" : "Deactivated"}`
      );
    } catch {
      toast.error("Failed to update status");

      setList((prev) =>
        prev.map((x) =>
          x.id === item.id
            ? {
                ...x,
                IsActive: !checked,
              }
            : x
        )
      );
    }
  };

  // =========================================
  // SORT
  // =========================================

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";

    setOrder(isAsc ? "desc" : "asc");

    setOrderBy(property);
  };

  // =========================================
  // RESET FILTER
  // =========================================

  const handleResetFilters = () => {
    setGlobalSearch("");

    setSearchText({});

    setShowAllFilters(false);

    setPage(0);
  };

  // =========================================
  // FILTER
  // =========================================

  const filteredList = list.filter((item) => {
    const globalMatch = Object.values(item)
      .join(" ")
      .toLowerCase()
      .includes(globalSearch.toLowerCase());

    const columnMatch = Object.keys(searchText).every((key) => {
      if (!searchText[key]) return true;

      const value = item[key]?.toString().toLowerCase() || "";

      return value.includes(searchText[key].toLowerCase());
    });

    return globalMatch && columnMatch;
  });

  // =========================================
  // SORTED LIST
  // =========================================

  const sortedList = [...filteredList].sort((a, b) => {
    if (!orderBy) return 0;

    let valueA = a[orderBy];

    let valueB = b[orderBy];

    if (typeof valueA === "string") valueA = valueA.toLowerCase();

    if (typeof valueB === "string") valueB = valueB.toLowerCase();

    if (valueA < valueB) return order === "asc" ? -1 : 1;

    if (valueA > valueB) return order === "asc" ? 1 : -1;

    return 0;
  });

  // =========================================
  // HEADER
  // =========================================

  const renderHeader = (label, field) => (
    <TableCell>
      <Box
        display="flex"
        alignItems="center"
        gap={0.5}
        sx={{
          color: "text.primary",
        }}
      >
        <TableSortLabel
          active={orderBy === field}
          direction={orderBy === field ? order : "asc"}
          onClick={() => handleSort(field)}
        >
          {label}
        </TableSortLabel>
      </Box>
    </TableCell>
  );

  return (
        <PageContainer title="Application Management">
    
    <Box>
      {/* HEADER */}

      <Card
        sx={{
          mb: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          <FeatherIcon icon="alert-circle" width="18" />

          <Box sx={{ ml: 1 }}>
            {editId ? "Edit Request Type" : "Add Request Type"}
          </Box>
        </Box>

        {/* FORM */}

        <CardContent
          sx={{
            padding: "10px 30px",
          }}
        >
          <Grid container spacing={2} alignItems="flex-start">
            {/* REQUEST TYPE */}

            <Grid item lg={3} md={12} sm={12}>
              <CustomFormLabel>Request Type</CustomFormLabel>

              <CustomTextField
                name="name"
                placeholder="Enter Request Type"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                fullWidth
                size="small"
                error={
                  formik.touched.name &&
                  Boolean(formik.errors.name)
                }
                helperText={
                  formik.touched.name && formik.errors.name
                }
              />
            </Grid>

            {/* STATUS */}

            <Grid item lg={2} md={12} sm={12}>
              <CustomFormLabel>Status</CustomFormLabel>

              <Switch
                checked={formik.values.IsActive}
                onChange={(e) =>
                  formik.setFieldValue("IsActive", e.target.checked)
                }
              />
            </Grid>

            {/* BUTTON */}

            <Grid
              item
              lg={2}
              md={12}
              sm={12}
              mt={5}
              display="flex"
              alignItems="flex-start"
              height="100%"
            >
              <Button
                variant="contained"
                onClick={formik.handleSubmit}
                disabled={!formik.isValid}
              >
                {editId ? "Update" : "Add"}
              </Button>

              <Button
                sx={{ ml: 2 }}
                variant="outlined"
                onClick={resetForm}
              >
                {editId ? "Cancel" : "Reset"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* TABLE */}

      <Card
        sx={{
          mt: 2,
          p: 0,
          overflow: "hidden",
        }}
      >
        {/* HEADER */}

        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1.5}
          sx={{
            backgroundColor: "primary.light",
            color: "primary.main",
          }}
        >
          {/* LEFT */}

          <Box display="flex" alignItems="center">
            <FeatherIcon icon="alert-circle" width="18" />

            <Box sx={{ ml: 1 }}>Existing Request Types</Box>
          </Box>

          {/* RIGHT */}

          <Box display="flex" alignItems="center">
            <TextField
              size="small"
              placeholder="Search..."
              value={globalSearch}
              onFocus={() => {
                setShowAllFilters(false);

                setSearchText({});
              }}
              onChange={(e) => {
                setGlobalSearch(e.target.value);

                setPage(0);
              }}
              sx={{
                width: 220,
                backgroundColor: "white",
                borderRadius: 1,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />

            {globalSearch && (
              <CloseIcon
                sx={{
                  cursor: "pointer",
                  ml: 1,
                }}
                onClick={() => setGlobalSearch("")}
              />
            )}

            <FilterAltIcon
              sx={{
                cursor: "pointer",
                ml: 2,
                color: showAllFilters
                  ? (theme) => theme.palette.primary.main
                  : "text.primary",
                transition: "0.2s",
              }}
              onClick={() => setShowAllFilters(!showAllFilters)}
            />

            <Button
              variant="outlined"
              size="small"
              sx={{
                ml: 2,
                height: 32,
              }}
              onClick={handleResetFilters}
            >
              Reset Filter
            </Button>
          </Box>
        </Box>

        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#FAFBFB",
                  }}
                >
                  {renderHeader("ID", "id")}

                  {renderHeader("Request Type", "name")}

                  <TableCell>Status</TableCell>

                  <TableCell align="center">Action</TableCell>
                </TableRow>

                {showAllFilters && (
                  <TableRow>
                    {["id", "name"].map((field) => (
                      <TableCell key={field}>
                        <TextField
                          size="small"
                          fullWidth
                          value={searchText[field] || ""}
                          onChange={(e) =>
                            setSearchText({
                              ...searchText,
                              [field]: e.target.value,
                            })
                          }
                          placeholder="Search..."
                          InputProps={{
                            endAdornment: searchText[field] && (
                              <InputAdornment position="end">
                                <CloseIcon
                                  fontSize="small"
                                  sx={{
                                    cursor: "pointer",
                                  }}
                                  onClick={() =>
                                    setSearchText({
                                      ...searchText,
                                      [field]: "",
                                    })
                                  }
                                />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </TableCell>
                    ))}

                    <TableCell />

                    <TableCell />
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {sortedList.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        No request types found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}

                {(rowsPerPage > 0
                  ? sortedList.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                  : sortedList
                ).map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.id}</TableCell>

                    <TableCell>{item.name}</TableCell>

                    <TableCell>
                      <Tooltip
                        title={item.IsActive ? "Active" : "Inactive"}
                      >
                        <Switch
                          checked={item.IsActive}
                          size="small"
                          onChange={(e) =>
                            handleToggleStatus(
                              item,
                              e.target.checked
                            )
                          }
                        />
                      </Tooltip>
                    </TableCell>

                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <EditIcon
                          sx={{
                            cursor: "pointer",
                            color: "primary.main",
                          }}
                          onClick={() => handleEdit(item)}
                        />
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={sortedList.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));

              setPage(0);
            }}
            showFirstButton
            showLastButton
          />
        </CardContent>
      </Card>
    </Box>
    </PageContainer>
  );
};

export default RequestTypeMaster;